import React from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../../redux/slices/NotificationSlice';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DropdownField from '../../../shared/DropdownField';
import TextInput from '../../../shared/TextInput';
import { InputTextarea } from 'primereact/inputtextarea';
import DatePicker from '../../../shared/DatePicker';
import CustomEditor from '../../../shared/CustomEditor';
import { Card } from 'primereact/card';
import Button from '../../../shared/CustomButton';
import {  FaSave, FaTimes, FaPaperclip } from 'react-icons/fa';
import { 
  helpDeskDepartments, 
  ticketPriorities, 
  departmentFields 
} from '../../../constants/QueueManagementConstants';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';

const FieldError = ({ touched, error }) => {
  if (!touched || !error) return null;
  return <small className="text-red-500 mt-1">{error}</small>;
};

export default function TicketCreationTab() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const fileUploadStyles = `
    .custom-file-upload .p-fileupload-buttonbar .p-button {
      color: #000000 !important;
    }
    .custom-file-upload .p-fileupload-buttonbar .p-button .p-button-label {
      color: #000000 !important;
    }
    .custom-file-upload .p-fileupload-content {
      color: #000000 !important;
    }
    .custom-file-upload .p-fileupload-empty {
      color: #000000 !important;
    }
  `;

  const initialValues = {
    department: '',
    priority: 'Medium',
    subject: '',
    description: '',
    assignee: '', // optional assignee added
    contactInfo: {
      name: 'Dr. Sarah Ahmed',
      email: 'sarah.ahmed@hospital.com',
      phone: '+92 300 1234567',
      department: 'Medical Staff',
    },
    relatedTicket: '',
    attachments: [],
    departmentSpecific: {},
  };

  const validationSchema = Yup.object().shape({
    department: Yup.string().required(t('Department is required')),
    priority: Yup.string().required(t('Priority is required')),
    subject: Yup.string().trim().required(t('Subject is required')),
    description: Yup.string().trim().required(t('Description is required')),
    assignee: Yup.string().trim().nullable(),
    // departmentSpecific: handled below
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validate: (values) => {
      const errors = {};
      const deptFields = departmentFields[values.department] || [];
      deptFields.forEach(field => {
        if (field.required && !values.departmentSpecific[field.name]) {
          if (!errors.departmentSpecific) errors.departmentSpecific = {};
          errors.departmentSpecific[field.name] = `${field.label} is required`;
        }
      });
      return errors;
    },
    onSubmit: (values, { resetForm }) => {
      const ticketData = {
        ...values,
        ticketId: `TK-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
        status: 'Open',
        createdDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        requester: values.contactInfo.name,
        assignee: values.assignee?.trim() || 'Unassigned',
      };
      const existing = JSON.parse(localStorage.getItem('tickets') || '[]');
      localStorage.setItem('tickets', JSON.stringify([ticketData, ...existing]));
      toast.success(t('Ticket created successfully!'));
      dispatch(addNotification({
        id: Date.now(),
        title: t('Ticket Created'),
        message: t('A new ticket has been created.') + ` (${ticketData.ticketId})`,
        type: 'created',
        priority: ticketData.priority,
        department: ticketData.department,
        timestamp: new Date().toISOString(),
        isRead: false,
        isMarkRead: false,
        ticketId: ticketData.ticketId,
        assignee: ticketData.assignee || '',
        requiresAction: false,
      }));
      resetForm();
    },
  });

  const renderDepartmentFields = () => {
    if (!formik.values.department) return null;
    const fields = departmentFields[formik.values.department] || [];
    return (
      <Card title={t(`${formik.values.department} Specific Information`)} className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(field => (
            <div key={field.name} className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.type === 'text' && (
                <TextInput
                  name={`departmentSpecific.${field.name}`}
                  value={formik.values.departmentSpecific[field.name] || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formik.touched.departmentSpecific?.[field.name] && formik.errors.departmentSpecific?.[field.name] ? 'p-invalid' : ''}
                />
              )}
              {field.type === 'number' && (
                <TextInput
                  name={`departmentSpecific.${field.name}`}
                  value={
                    formik.values.departmentSpecific[field.name] !== undefined && formik.values.departmentSpecific[field.name] !== null
                      ? formik.values.departmentSpecific[field.name]
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Allow empty string for clearing; otherwise retain only digits (optional single dot if decimals needed later)
                    const cleaned = raw.replace(/[^0-9]/g, '');
                    formik.setFieldValue(`departmentSpecific.${field.name}`,
                      cleaned === '' ? '' : cleaned
                    );
                  }}
                  onBlur={formik.handleBlur}
                  placeholder={t(`Enter ${field.label}`)}
                  className={formik.touched.departmentSpecific?.[field.name] && formik.errors.departmentSpecific?.[field.name] ? 'p-invalid' : ''}
                />
              )}
              {field.type === 'date' && (
                <DatePicker
                  name={`departmentSpecific.${field.name}`}
                  value={formik.values.departmentSpecific[field.name] || null}
                  onChange={e => formik.setFieldValue(`departmentSpecific.${field.name}`, e.value)}
                  onBlur={formik.handleBlur}
                  className={formik.touched.departmentSpecific?.[field.name] && formik.errors.departmentSpecific?.[field.name] ? 'p-invalid' : ''}
                  placeholder={t(`Select ${field.label}`)}
                />
              )}
              {field.type === 'textarea' && (
                <InputTextarea
                  name={`departmentSpecific.${field.name}`}
                  value={formik.values.departmentSpecific[field.name] || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={3}
                  className={formik.touched.departmentSpecific?.[field.name] && formik.errors.departmentSpecific?.[field.name] ? 'p-invalid' : ''}
                />
              )}
              {field.type === 'select' && (
                <DropdownField
                  name={`departmentSpecific.${field.name}`}
                  value={formik.values.departmentSpecific[field.name] || ''}
                  onChange={e => formik.setFieldValue(`departmentSpecific.${field.name}`, e.value)}
                  options={field.options?.map(opt => ({ label: opt, value: opt }))}
                  placeholder={`Select ${field.label}`}
                  className={formik.touched.departmentSpecific?.[field.name] && formik.errors.departmentSpecific?.[field.name] ? 'p-invalid' : ''}
                />
              )}
              {field.type === 'file' && (
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-blue-400 rounded-lg p-4 bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
                  <label htmlFor={`dept-file-input-${field.name}`} className="cursor-pointer flex flex-col items-center w-full">
                    <FaPaperclip className="text-2xl text-blue-400 mb-2" />
                    <span className="text-base font-medium text-blue-700 mb-1">{t('Drag and drop')} {field.label}</span>
                    <span className="text-xs text-gray-500">{t('or click to browse')}</span>
                    <input
                      id={`dept-file-input-${field.name}`}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      className="hidden"
                      onChange={e => {
                        const files = Array.from(e.target.files);
                        formik.setFieldValue(`departmentSpecific.${field.name}`, files.map(f => f.name));
                        e.target.value = null;
                      }}
                    />
                  </label>
                  <span className="text-xs text-gray-400 mt-2">{t('Max file size')}: 10MB</span>
                  {Array.isArray(formik.values.departmentSpecific[field.name]) && formik.values.departmentSpecific[field.name].length > 0 && (
                    <div className="mt-3 w-full">
                      <ul className="space-y-1">
                        {formik.values.departmentSpecific[field.name].map((file, idx) => (
                          <li key={idx} className="flex items-center gap-2 bg-blue-100 rounded px-2 py-1 text-blue-800">
                            <FaPaperclip className="text-blue-400" />
                            <span className="truncate max-w-xs">{file}</span>
                            <button
                              type="button"
                              className="ml-auto text-red-500 hover:text-red-700 text-xs font-bold px-2"
                              onClick={() => {
                                const files = formik.values.departmentSpecific[field.name].filter((_, i) => i !== idx);
                                formik.setFieldValue(`departmentSpecific.${field.name}`, files);
                              }}
                              aria-label={t('Remove file')}
                            >
                              &times;
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {/* {formik.touched.departmentSpecific?.[field.name] && formik.errors.departmentSpecific?.[field.name] && (
                <small className="text-red-500 mt-1">{formik.errors.departmentSpecific[field.name]}</small>
              )} */}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: fileUploadStyles }} />
      <div>
  <FormikProvider value={formik}>
  <form onSubmit={formik.handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <Card title={t('Basic Information')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t('Department')} <span className="text-red-500">*</span>
                </label>
                <DropdownField
                  name="department"
                  value={formik.values.department}
                  onChange={e => formik.setFieldValue('department', e.value)}
                  onBlur={formik.handleBlur}
                  options={helpDeskDepartments}
                  placeholder={t('Select Department')}
                  className={formik.touched.department && formik.errors.department ? 'p-invalid' : ''}
                />
               
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t('Priority')} <span className="text-red-500">*</span>
                </label>
                <DropdownField
                  name="priority"
                  value={formik.values.priority}
                  onChange={e => formik.setFieldValue('priority', e.value)}
                  onBlur={formik.handleBlur}
                  options={ticketPriorities.map(priority => ({
                    label: t(priority.value),
                    value: priority.value
                  }))}
                  placeholder={t('Select Priority')}
                  className={formik.touched.priority && formik.errors.priority ? 'p-invalid' : ''}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t('Assigned To')} <span className="text-gray-400 text-xs">({t('Optional')})</span>
                </label>
                <TextInput
                  name="assignee"
                  value={formik.values.assignee}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('Enter assignee name (e.g., John Doe)')}
                />
                {formik.touched.assignee && formik.errors.assignee && (
                  <small className="text-red-500 mt-1">{formik.errors.assignee}</small>
                )}
              </div>
              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t('Subject')} <span className="text-red-500">*</span>
                </label>
                <TextInput
                  name="subject"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('Enter ticket subject (e.g., Network connectivity issue)')}
                  className={formik.touched.subject && formik.errors.subject ? 'p-invalid' : ''}
                />
              </div>
              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t('Detailed Description')} <span className="text-red-500">*</span>
                </label>
                <CustomEditor
                  name="description"
                  value={formik.values.description}
                  onChange={val => formik.setFieldValue('description', val)}
                  onBlur={formik.handleBlur}
                  height="200px"
                  className={formik.touched.description && formik.errors.description ? 'p-invalid' : ''}
                />
                < FieldError touched={formik.touched.description} error={formik.errors.description} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t('Related Ticket ID')} ({t('Optional')})
                </label>
                <TextInput
                  name="relatedTicket"
                  value={formik.values.relatedTicket}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('TK-2025-XXX')}
                />
              </div>
            </div>
          </Card>
          {/* Contact Information (Pre-filled) */}
          <Card title={t('Contact Information')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">{t('Name')}</label>
                <TextInput name="contactInfo.name" value={formik.values.contactInfo.name} disabled useFormik={true} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">{t('Email')}</label>
                <TextInput name="contactInfo.email" value={formik.values.contactInfo.email} disabled useFormik={true} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">{t('Phone')}</label>
                <TextInput name="contactInfo.phone" value={formik.values.contactInfo.phone} disabled useFormik={true} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">{t('Department')}</label>
                <TextInput name="contactInfo.department" value={formik.values.contactInfo.department} disabled useFormik={true} />
              </div>
            </div>
          </Card>
          {/* Department-Specific Fields */}
          {renderDepartmentFields()}
          {/* File Attachments */}
          <Card title={t('Attachments')}>
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-blue-400 rounded-lg p-6 bg-blue-50 hover:bg-blue-100 transition-colors duration-200">
              <label htmlFor="file-input" className="cursor-pointer flex flex-col items-center">
                <FaPaperclip className="text-3xl text-blue-400 mb-2" />
                <span className="text-base font-medium text-blue-700 mb-1">{t('Drag and drop files here')}</span>
                <span className="text-xs text-gray-500">{t('or click to browse')}</span>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files);
                    formik.setFieldValue('attachments', [...formik.values.attachments, ...files.map(f => f.name)]);
                    e.target.value = null;
                  }}
                />
              </label>
              <span className="text-xs text-gray-400 mt-2">{t('Max file size')}: 10MB</span>
            </div>
            {formik.values.attachments.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2 font-semibold">{t('Attached Files')}:</p>
                <ul className="space-y-2">
                  {formik.values.attachments.map((file, index) => (
                    <li key={index} className="flex items-center gap-2 bg-blue-100 rounded px-3 py-1 text-blue-800">
                      <FaPaperclip className="text-blue-400" />
                      <span className="truncate max-w-xs">{file}</span>
                      <button
                        type="button"
                        className="ml-auto text-red-500 hover:text-red-700 text-xs font-bold px-2"
                        onClick={() => {
                          const newFiles = formik.values.attachments.filter((_, i) => i !== index);
                          formik.setFieldValue('attachments', newFiles);
                        }}
                        aria-label={t('Remove file')}
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={() => window.history.back()}
              label={t('Cancel')}
              icon={<FaTimes />}
              buttonType="secondary"
            />
            <Button
              type="submit"
              label={t('Create Ticket')}
              icon={<FaSave />}
              buttonType="primary"
            />
          </div>
        </form>
        </FormikProvider>
      </div>
    </div>
  );
}